import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { matchPath, useLocation } from 'react-router-dom';
import { routeTitles } from '../Data/routeTitles';
import useAuth from '../Hooks/useAuth';

/*
 * Unified breadcrumb trail system:
 *
 * Non-archives pages: Home → Current Page
 * Archives listing/issue view: Home → Archives → Volume X → Issue Y
 * Article page: Home → Archives → Volume X → Issue Y → Article Title
 *
 * Intermediate pages are not shown; trail adapts based on current location.
 * Dynamic labels via useBreadcrumbLabel override the auto-generated ones.
 */

const STORAGE_KEY = 'itme.breadcrumb.current';

const HOME = { key: '/', pathname: '/', search: '', label: 'Home' };

const BreadcrumbContext = createContext({
    trail: [HOME],
    setLabelForLocation: () => {},
});

/* A location is identified by pathname + search: the Archives issue view lives
   at /archives?vol=1&iss=1, so the query string is part of its identity. */
const locationKey = (location) => `${location.pathname}${location.search}`;

const titleCase = (segment) =>
    segment.replace(/[-_]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

export const resolveLabel = (pathname) => {
    const known = routeTitles.find((route) => matchPath({ path: route.path, end: true }, pathname));
    if (known) return known.label;

    const last = pathname.split('/').filter(Boolean).pop();
    if (!last) return HOME.label;
    try {
        return titleCase(decodeURIComponent(last));
    } catch (err) {
        return titleCase(last);
    }
};

/* Build breadcrumb trail, with special handling for archives */
const buildTrail = (entry, location) => {
    if (entry.key === HOME.key) return [HOME];

    // Check if this is an archives route
    const isArchivesRoute = location.pathname.startsWith('/archives');

    if (isArchivesRoute) {
        // Build full archives hierarchy: Home → Archives → Volume X → Issue Y [→ Article]
        const trail = [
            HOME,
            {
                key: '/archives',
                pathname: '/archives',
                search: '',
                label: 'Archives',
            },
        ];

        // Extract volume and issue from URL or query params
        const pathMatch = location.pathname.match(/\/archives\/vol\/(\d+)\/iss\/(\d+)(?:\/art\/(\d+))?/);
        const params = new URLSearchParams(location.search);
        const vol = pathMatch ? parseInt(pathMatch[1], 10) : parseInt(params.get('vol') || '0', 10);
        const iss = pathMatch ? parseInt(pathMatch[2], 10) : parseInt(params.get('iss') || '0', 10);
        const artNum = pathMatch ? parseInt(pathMatch[3], 10) : null;

        // Add Volume if present
        if (vol) {
            trail.push({
                key: `/archives/vol/${vol}`,
                pathname: '/archives',
                search: `?vol=${vol}`,
                label: `Volume ${vol}`,
            });
        }

        // Add Issue if present
        if (iss) {
            trail.push({
                key: `/archives/vol/${vol}/iss/${iss}`,
                pathname: '/archives',
                search: `?vol=${vol}&iss=${iss}`,
                label: `Issue ${iss}`,
            });
        }

        // Add Article if present
        if (artNum) {
            // On article page, entry.label will be set by the page via useBreadcrumbLabel
            // It will contain the article title, not just "Article N"
            trail.push({
                key: entry.key,
                pathname: entry.pathname,
                search: entry.search,
                label: entry.label, // Article title from useBreadcrumbLabel
            });
        }

        return trail;
    }

    // Non-archives pages: just Home → Current Page
    return [HOME, entry];
};

const readStoredEntry = () => {
    try {
        const raw = sessionStorage.getItem(STORAGE_KEY);
        const parsed = raw ? JSON.parse(raw) : null;
        if (parsed && parsed.key) return parsed;
    } catch (err) {
        // sessionStorage unavailable (private mode, blocked storage) — start fresh.
    }
    return null;
};

export const BreadcrumbProvider = ({ children }) => {
    const location = useLocation();
    const { isAuthenticated, loading } = useAuth();

    // Trail is always: [HOME, currentEntry] or just [HOME]
    const [trail, setTrail] = useState(() => {
        const stored = readStoredEntry();
        return stored ? [HOME, stored] : [HOME];
    });

    /* Labels supplied by pages via useBreadcrumbLabel, keyed by location.
       React runs child effects before parent ones, so a page announces its
       label before this provider has recorded the location — the override is
       parked here and picked up when the entry is created. */
    const overrides = useRef({});

    const key = locationKey(location);
    const { pathname, search } = location;

    // On every location change, build a new trail with Home + current page.
    useEffect(() => {
        const entry = {
            key,
            pathname,
            search,
            label: overrides.current[key] || resolveLabel(pathname),
        };
        setTrail(buildTrail(entry, location));
    }, [key, pathname, search, location]);

    // Persist the current page entry so it survives a refresh.
    useEffect(() => {
        try {
            const current = trail.length > 1 ? trail[1] : null;
            if (current) {
                sessionStorage.setItem(STORAGE_KEY, JSON.stringify(current));
            } else {
                sessionStorage.removeItem(STORAGE_KEY);
            }
        } catch (err) {
            // Storage unavailable — the trail still works for this session.
        }
    }, [trail]);

    // Signing in or out swaps the route tree in AuthRoutes. Reset to Home.
    const lastAuth = useRef(null);
    useEffect(() => {
        if (loading) return;
        if (lastAuth.current === null) {
            lastAuth.current = isAuthenticated;
            return;
        }
        if (lastAuth.current !== isAuthenticated) {
            lastAuth.current = isAuthenticated;
            setTrail([HOME]);
        }
    }, [isAuthenticated, loading]);

    /* Update the label of the current page. */
    const setLabelForLocation = useCallback((entryKey, label) => {
        if (!label) return;
        overrides.current[entryKey] = label;
        setTrail((prev) => {
            if (prev.length < 2) return prev;
            const current = prev[1];
            if (current.key !== entryKey) return prev;
            const updated = { ...current, label };
            return [HOME, updated];
        });
    }, []);

    const value = useMemo(() => ({ trail, setLabelForLocation }), [trail, setLabelForLocation]);

    return <BreadcrumbContext.Provider value={value}>{children}</BreadcrumbContext.Provider>;
};

export const useBreadcrumbs = () => useContext(BreadcrumbContext);

/**
 * Give the current page a breadcrumb label of its own.
 * Pass a falsy value to fall back to the route registry.
 *
 *   useBreadcrumbLabel(`Issue ${issue.issue}`);
 */
export const useBreadcrumbLabel = (label) => {
    const { setLabelForLocation } = useContext(BreadcrumbContext);
    const location = useLocation();
    const key = locationKey(location);

    useEffect(() => {
        setLabelForLocation(key, label);
    }, [key, label, setLabelForLocation]);
};

export default BreadcrumbContext;
